(ns app.social-scheduling.register
  "UI register entries for the generic social scheduling slice."
  (:require [app.bundle-social.platforms :as social-platforms]
            [app.social-scheduling.page :as page]
            [app.upload-manager.core :as upload-manager]
            [cljs.core.async :refer [<!]]
            [clojure.string :as str]
            [parts.replicant.command-query :as command-query]
            [parts.replicant.query :as query]
            [storrito.instagram-connect.account-sync :as account-sync]
            [storrito.state.local-storage :as local-storage])
  (:require-macros [cljs.core.async.macros :refer [go]]))

(def ^:private selected-destinations-key
  :social-scheduling/selected-destinations)

(def ^:private remembered-options-key
  :social-scheduling/remembered-options)

(def ^:private max-reel-cover-size-bytes
  (* 10 1024 1024))

(defn- tiktok-creator-info-query
  [account-id]
  {:query/kind :query/tiktok-creator-info
   :query/data {:tiktok.user/id account-id}})

(defn- instagram-highlights-query
  [user-id]
  {:query/kind :query/social-scheduling-highlights
   :query/data {:instagram.user/id user-id}})

(defn- query-tiktok-creator-info!
  [event-handler selected]
  (when event-handler
    (doseq [account-id (keep :tiktok.user/id selected)]
      (event-handler {} [[:data/query (tiktok-creator-info-query account-id)]]))))

(defn- query-instagram-highlights!
  [store event-handler selected]
  (when event-handler
    (doseq [user-id (keep (fn [entry]
                            (when (= :instagram (:destination/kind entry))
                              (:instagram.user/id entry)))
                          selected)]
      (let [q (instagram-highlights-query user-id)]
        (when-not (or (query/available? @store q)
                      (query/loading? @store q))
          (event-handler {} [[:data/query q]]))))))

(def ^:private caption-link-delay-ms
  "How long the caption must stay unchanged before the server is asked
   whether it contains a link: not a request per keystroke."
  500)

(defonce ^:private caption-link-timeout
  (atom nil))

(defn- store-caption-link-answer!
  "Puts the `answer` to `q` into `::page/caption-links`, see
   `page/caption-link-answers`, and drops `q` from the query log: the
   answer is kept there, and every typing pause asks for another text."
  [store kind q answer]
  (swap! store
         (fn [state]
           (let [current (page/caption-link-query state
                                                  (some->> (page/social-scheduling-query state)
                                                           (query/get-result state))
                                                  kind)]
             (-> state
                 (update ::page/caption-links
                         page/caption-link-answers kind q current answer)
                 (update :ui.query/log dissoc q))))))

(defn- query-caption-links!
  "Asks the server, once typing paused, whether the caption contains a
   link, for every selected post type that bills one (X); see
   `page/caption-link-queries` and `page/caption-link-answers`."
  [{:keys [store] :as w}]
  (some-> @caption-link-timeout js/clearTimeout)
  (reset! caption-link-timeout
          (js/setTimeout
            (fn []
              (reset! caption-link-timeout nil)
              (let [state @store
                    result (some->> (page/social-scheduling-query state)
                                    (query/get-result state))]
                (doseq [[kind q] (page/caption-link-queries state result)
                        ;; Answered already, e.g. on a destination toggle.
                        :when (not= (get-in q [:query/data :text])
                                    (get-in state [::page/caption-links kind :social-caption/text]))]
                  (-> (command-query/query-backend (assoc w :action [:data/query q]))
                      (.then #(store-caption-link-answer! store kind q (query/get-result % q)))
                      ;; `query-backend` stores a failure as the answer
                      ;; and resolves; this only guards a rejection.
                      (.catch #(store-caption-link-answer! store kind q nil))))))
            caption-link-delay-ms)))

(defn- account-selector-destination
  "Fallback destination from the account picked in the shared account
   selector (/ui/agenda, /ui/bulk), so this page shows the same
   selection. Only the key, kind and platform id are known here;
   reconcile-selected-destinations! completes the entry from the query
   result once it arrives and drops it when the account is unavailable."
  []
  (let [ls @local-storage/state
        account-key (or (:schedule/account-key ls)
                        (some->> (:instagram.user/id ls)
                                 (str "instagram:")))]
    (when-let [[_ kind id] (and account-key
                                (re-matches #"([a-z-]+):(.+)" account-key))]
      (case kind
        "instagram" {:destination/key account-key
                     :destination/kind :instagram
                     :instagram.user/id id
                     :share-to-fb? false}
        "tiktok" {:destination/key account-key
                  :destination/kind :tiktok
                  :tiktok.user/id id}
        "youtube" {:destination/key account-key
                   :destination/kind :youtube
                   :youtube.user/id id}
        ;; An account of a bundle.social platform from the agenda
        ;; selector is offered with the platform's first post type (a
        ;; Facebook page as a Reel); the other post types of the same
        ;; account are one click away.
        (when-let [post-type (first (:social-platform/post-types
                                      (social-platforms/by-key (keyword kind))))]
          (when-let [uuid (uuid id)]
            {:destination/key (str (name (:post-type/kind post-type)) ":" uuid)
             :destination/kind (:post-type/kind post-type)
             :social.account/uuid uuid}))))))

(defn- save-selected-destinations!
  [store selected]
  (let [selected* (vec selected)]
    (swap! store assoc ::page/selected-destinations selected*)
    ;; The highlight choice is a per-story decision and must not stick
    ;; across visits: a remembered `:highlight-id` is submitted with
    ;; every future story although the highlight select shows '-'.
    (swap! local-storage/state assoc selected-destinations-key
           (mapv #(dissoc % :highlight-id) selected*))))

(defn- reconcile-selected-destinations!
  [store result]
  (when (and (:story-media/eid result)
             (contains? @store ::page/selected-destinations))
    (save-selected-destinations!
      store
      (page/with-default-destination
        (page/reconcile-selected-destinations @store result)
        result))))

(defn- query-social-scheduling!
  [{:keys [store action ui/event-handler] :as w}]
  (let [[_ q] action]
    (.then (command-query/query-backend (assoc w :action [:data/query q]))
           (fn [state]
             (reconcile-selected-destinations! store (query/get-result state q))
             (query-instagram-highlights! store
                                          event-handler
                                          (::page/selected-destinations @store))))))

(defn- submit!
  ;; Re-computes the commands from the current state instead of trusting
  ;; a payload from the click action: the rendered button can hold the
  ;; hiccup of an older render, and submitting its command list posted
  ;; stories to an already de-selected account.
  [{:keys [store action] :as w}]
  (let [[_ q] action
        state @store
        result (query/get-result state q)
        {:keys [pending-cmds can-submit?]} (page/submission state result)
        cmds pending-cmds]
    (when (and can-submit?
               (seq cmds)
               (not (::page/submitting? @store)))
      (swap! store assoc
             ::page/submitting? true
             ::page/submitted? nil)
      (-> (js/Promise.all
            (to-array
              (map (fn [cmd]
                     (command-query/issue-command
                       (assoc w
                              :action [:data/command
                                       cmd
                                       {:on-success [[:store/assoc ::page/submitted? true]
                                                     [:social-scheduling/query q]]
                                        :on-error [[:store/assoc ::page/submitted? nil]
                                                   [:ui/show-error]]}])))
                   cmds)))
          (.finally #(swap! store assoc ::page/submitting? false))))))

(defn- cover-image-file?
  [file]
  (let [content-type (.-type file)
        file-name (str/lower-case (or (.-name file) ""))]
    (or (#{"image/jpeg" "image/png"} content-type)
        (str/ends-with? file-name ".jpg")
        (str/ends-with? file-name ".jpeg")
        (str/ends-with? file-name ".png"))))

(defn- upload-reel-cover!
  [store file]
  (cond
    (not (cover-image-file? file))
    (swap! store assoc ::page/reels-cover-upload-error "Please upload a JPG or PNG image.")

    (> (.-size file) max-reel-cover-size-bytes)
    (swap! store assoc ::page/reels-cover-upload-error "Cover image must be under 10 MB.")

    :else
    (do
      (swap! store assoc
             ::page/reels-cover-uploading? true
             ::page/reels-cover-upload-error nil)
      (go
        (try
          (let [blob-uuid (<! (upload-manager/upload-blob!
                                {:manager-state upload-manager/upload-reel-state
                                 :blob file
                                 :name (.-name file)}))]
            (swap! store assoc
                   ::page/reels-cover-blob-id blob-uuid
                   ::page/reels-cover-name (.-name file)
                   ::page/reels-cover-uploading? false
                   ::page/reels-cover-upload-error nil))
          (catch :default e
            (swap! store assoc
                   ::page/reels-cover-uploading? false
                   ::page/reels-cover-upload-error (or (some-> e .-message)
                                                       "Cover photo upload failed."))))))))

(defn- sync-instagram-account!
  "Fetches the linked Facebook pages and highlight albums of an Instagram
   account from Instagram (the same sync as in the Instagram Accounts
   panel) and re-queries the page so the Story options show them."
  [{:keys [store action ui/event-handler]}]
  (let [[_ user-id] action]
    (when-not (contains? (::page/syncing-user-ids @store) user-id)
      (swap! store update ::page/syncing-user-ids (fnil conj #{}) user-id)
      (go
        (<! (account-sync/request-sync! {:ig-user-id user-id
                                         :manually-sync true}))
        (swap! store (fn [state]
                       (-> state
                           (update ::page/syncing-user-ids disj user-id)
                           (update ::page/synced-user-ids (fnil conj #{}) user-id))))
        (when event-handler
          (let [q (page/social-scheduling-query @store)]
            (event-handler {} (cond-> [[:data/query (instagram-highlights-query user-id)]]
                                q (conj [:social-scheduling/query q])))))))))

(defn- set-highlight-id
  [selected user-id highlight-id]
  (mapv (fn [entry]
          (if (and (= :instagram (:destination/kind entry))
                   (= (:instagram.user/id entry) user-id))
            (if (seq highlight-id)
              (assoc entry :highlight-id highlight-id)
              (dissoc entry :highlight-id))
            entry))
        selected))

(def register
  [page/page
   {:ui.action/kind :social-scheduling/query
    :ui.action/handler query-social-scheduling!}
   {:ui.action/kind :social-scheduling/submit
    :ui.action/handler submit!}
   {:ui.action/kind :social-scheduling/query-caption-links
    :ui.action/handler query-caption-links!}
   {:ui.action/kind :social-scheduling/restore-selected-destinations
    :ui.action/handler
    (fn [{:keys [store ui/event-handler]}]
      ;; Also drop `:highlight-id` values that were persisted before
      ;; `save-selected-destinations!` stripped them.
      (when-let [selected (or (seq (mapv #(dissoc % :highlight-id)
                                         (get @local-storage/state selected-destinations-key)))
                              (some-> (account-selector-destination) vector))]
        (swap! store assoc ::page/selected-destinations (vec selected))
        (query-tiktok-creator-info! event-handler selected)
        (query-instagram-highlights! store event-handler selected)))}
   {:ui.action/kind :social-scheduling/restore-options
    :ui.action/handler
    (fn [{:keys [store]}]
      (swap! store merge (page/remembered-options
                           (get @local-storage/state remembered-options-key))))}
   {:ui.action/kind :social-scheduling/remember-options
    :ui.action/handler
    (fn [{:keys [store]}]
      (swap! local-storage/state assoc remembered-options-key
             (page/remembered-options @store)))}
   {:ui.action/kind :social-scheduling/toggle-destination
    :ui.action/handler
    ;; Recomputes the selection from the current store instead of trusting
    ;; a list computed during render: a click that lands before the next
    ;; render otherwise reverts newer selection changes (a de-selected
    ;; account came back and the story was posted to it).
    (fn [{:keys [store action ui/event-handler] :as w}]
      (let [[_ destination] action
            current (or (::page/selected-destinations @store) [])
            newly-added-tiktok? (and (= :tiktok (:destination/kind destination))
                                     (not (page/selected-destination? current
                                                                      destination)))
            selected (page/toggle-destination current destination)]
        (save-selected-destinations! store selected)
        (swap! store assoc ::page/submitted? nil)
        (when newly-added-tiktok?
          (swap! store assoc ::page/tiktok-privacy-level nil))
        (query-tiktok-creator-info! event-handler selected)
        (query-instagram-highlights! store event-handler selected)
        ;; An X account selected after the caption was typed.
        (query-caption-links! w)))}
   {:ui.action/kind :social-scheduling/toggle-fb-destination
    :ui.action/handler
    (fn [{:keys [store action]}]
      (let [[_ user-id linkage] action
            selected (page/toggle-fb-destination
                       (or (::page/selected-destinations @store) [])
                       user-id
                       linkage)]
        (save-selected-destinations! store selected)
        (swap! store assoc ::page/submitted? nil)))}
   {:ui.action/kind :social-scheduling/upload-reel-cover
    :ui.action/handler
    (fn [{:keys [store action]}]
      (when-let [file (first (second action))]
        (upload-reel-cover! store file)))}
   {:ui.action/kind :social-scheduling/sync-instagram-account
    :ui.action/handler sync-instagram-account!}
   {:ui.action/kind :social-scheduling/set-highlight-id
    :ui.action/handler
    (fn [{:keys [store action]}]
      (let [[_ user-id highlight-id] action
            selected (set-highlight-id (or (::page/selected-destinations @store) [])
                                       user-id
                                       highlight-id)]
        (save-selected-destinations! store selected)))}])
